export interface SerpSearchResult {
  rank: number
  title: string
  url: string
  description?: string
}

export interface SummarizationResult {
  id: string
  input_query: string
  summary: string[]
}

export interface ResearchQueryPayload {
  title: string
  outline_header: string
}

export interface ResearchQueryInResponse {
  query_string: string
  raw_search_results?: SerpSearchResult[]
  research_result?: SummarizationResult
}

/**
 * Individual keyword search result item used by blog outline generators
 */
export interface KeywordResearchResultItem {
  title: string
  url?: string
  h2_headers: string[]
  rank?: number
}

/**
 * Keyword search result used by blog outline generators
 */
export interface KeywordResearchResult {
  organic: KeywordResearchResultItem[]
  people_also_ask: KeywordResearchResultItem[]
  queries?: string[]
}

/**
 * Payload for product research endpoint
 */
export interface ProductResearchPayload {
  product_title?: string
  brand: string
  product_id: string
  tags?: string[]
  product_details?: Record<string, any>
}

export enum ProductResearchSourceType {
  web_search = 'web_search',
  specific_url = 'specific_url',
  image = 'image',
  upc = 'upc'
}

/**
 * The fallback order for single source bulk enrichment.
 * As of now it is upc -> image -> specific_url -> web_search
 */
export const bulkEnrichmentFallbackOrder = [
  ProductResearchSourceType.upc,
  ProductResearchSourceType.image,
  ProductResearchSourceType.specific_url,
  ProductResearchSourceType.web_search
]

/**
 * Product research result
 */
export interface ProductResearchResult {
  source_urls?: string[]
  source_url?: string
  image_src?: string
  upc?: string
  content: string
  source_type: ProductResearchSourceType
}

export interface ProductResearchResultWithProductId {
  product_research_result: ProductResearchResult
  product_id: string
}

export interface SeoRelatedKeywordInCreateRequest {
  /**
   * Keyword to generate related keywords for
   */
  keyword: string
  /**
   * Country ISO code
   */
  country_iso_code: string
}

/**
 * SEO related keyword to a primary keyword.
 */
export interface SeoRelatedKeyword {
  /**
   * Unique ID of the related keyword
   */
  id: string
  /**
   * Actual related keyword
   */
  keyword: string
  /**
   * Number of times the keyword appears
   * in competitor sites.
   *
   * @note Refer to the backend documentation
   * for more information on how this is calculated.
   */
  frequency_in_external_sites: number
  /**
   * Google search volume for the keyword.
   */
  search_volume?: number
  /**
   * Keyword difficulty.
   *
   * It can be one of the following:
   * - easy
   * - medium
   * - hard
   *
   * Refers to how difficult it is to
   * rank for the keyword.
   */
  competition?: string
  /**
   * Keyword difficulty index.
   *
   * This is a score between 0 and 100
   * that indicates how difficult it is to
   * rank for the keyword.
   */
  competition_index?: number
  /**
   * Timestamp when the related keyword
   * object was created in backend.
   */
  created_at: number
}

/**
 * Represents the info about a single search result from a SERP
 * for a primary keyword.
 *
 * This differs from a normal SERP search result representation in that it
 * contains additional information such as the frequency of the
 * primary keyword in the search result.
 */
export interface SeoSerpSearchResult {
  /**
   * Unique ID of the SERP search result.
   */
  id: string
  /**
   * Title of the SERP search result.
   */
  title: string
  /**
   * URL of the SERP search result.
   */
  url: string
  /**
   * Rank of the SERP search result.
   */
  rank?: number
  /**
   * H2 headers of the SERP search result.
   */
  h2_headers: string[]
  /**
   * Word count of the SERP search result.
   */
  word_count: number
  /**
   * Number of links in the SERP search result.
   */
  num_of_links: number
  /**
   * Number of images in the SERP search result.
   */
  num_of_images: number
  /**
   * Frequency of the primary keyword in the SERP search result.
   */
  frequency_of_primary_keyword: number
}

/**
 * Represents the info about a single search result from a PAA
 * for a primary keyword.
 */
export interface SeoPaaSearchResult {
  /**
   * Unique ID of the PAA search result.
   */
  id: string
  /**
   * Title of the PAA search result.
   */
  title: string
  /**
   * Rank of the PAA search result.
   */
  rank?: number
}

/**
 * Response interface for a research
 * on a primary keyword.
 */
export interface PrimaryKeywordResearchSerpAndPaaResult {
  /**
   * Unique ID of the primary keyword.
   */
  id: string
  /**
   * Country ISO code used for SERP search.
   */
  country_iso_code: string
  /**
   * Primary keyword used for the research.
   */
  primary_keyword: string
  /**
   * SERP search results for the primary keyword.
   */
  serp_search_results: SeoSerpSearchResult[]
  /**
   * PAA search results for the primary keyword.
   * (People Also Ask)
   */
  paa_search_results: SeoPaaSearchResult[]
  /**
   * Timestamp when the research result
   * was created.
   */
  created_at: number
  /**
   * Timestamp when the research result
   * was resolved.
   */
  resolved_at?: number
  /**
   * Boolean flag indicating whether
   * the research result had an error.
   */
  error: boolean
  /**
   * Error message if the research result
   * had an error.
   */
  error_message?: string
  /**
   * Boolean flag indicating whether
   * the research result has been deleted.
   */
  deleted: boolean
  /**
   * Timestamp when the research result
   * was deleted.
   */
  deleted_at?: number
}

/**
 * Response interface for a related keyword query
 * on a primary keyword.
 */
export interface PrimaryKeywordResearchRelatedKeywordsResultInResponse {
  /**
   * Unique ID of the primary keyword.
   */
  id: string
  /**
   * Country ISO code used for SERP search.
   */
  country_iso_code: string
  /**
   * Primary keyword used for the research.
   */
  primary_keyword: string
  /**
   * Related keywords to the primary keyword.
   */
  related_keywords: SeoRelatedKeyword[]
  /**
   * Timestamp when the research result
   * was created.
   */
  created_at: number
  /**
   * Timestamp when the research result
   * was resolved.
   */
  resolved_at?: number
  /**
   * Boolean flag indicating whether
   * the research result had an error.
   */
  error: boolean
  /**
   * Error message if the research result
   * had an error.
   */
  error_message?: string
  /**
   * Boolean flag indicating whether
   * the research result has been deleted.
   */
  deleted: boolean
  /**
   * Timestamp when the research result
   * was deleted.
   */
  deleted_at?: number
}

export const NO_PRODUCTS_MET_REQUIREMENT_ERROR_TYPE =
  'no_products_met_requirement'

export enum BatchJobType {
  add_products = 'add_products',
  upload_images = 'upload_images',
  export_products_csv = 'export_products_csv',
  export_products_xlsx = 'export_products_xlsx',
  describe = 'describe',
  seo_keyword_suggestion = 'seo_keyword_suggestion',
  enrich_from_web = 'enrich_from_web',
  enrich_from_url = 'enrich_from_url',
  enrich_from_image = 'enrich_from_image',
  enrich_from_upc = 'enrich_from_upc',
  enrich_with_fallback = 'enrich_with_fallback',
  translate_product_descriptions = 'translate_product_descriptions'
}

export interface BatchSingleProductFinishedResponse {
  job_id: string
  product_id: string
  job_type: BatchJobType
}

export const PRODUCT_RESEARCH_SOURCE_TYPE_TO_BATCH_JOB_TYPE_MAPPING: Record<
  ProductResearchSourceType,
  BatchJobType
> = {
  [ProductResearchSourceType.web_search]: BatchJobType.enrich_from_web,
  [ProductResearchSourceType.specific_url]: BatchJobType.enrich_from_url,
  [ProductResearchSourceType.image]: BatchJobType.enrich_from_image,
  [ProductResearchSourceType.upc]: BatchJobType.enrich_from_upc
}
